/**
 * Converts a time string "HH:mm" to total minutes since midnight.
 * e.g., "14:30" -> (14 * 60) + 30 = 870
 */
const timeStringToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return (hours * 60) + minutes;
};

const applyDoWDefaults = (days) => {

    let ret = {};

    ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].forEach((day) => {
        ret[day] = !!days[day]
    });

    console.log(ret)
    return ret;
}

/**
 * Formats the react-hook-form data to match the Mongoose schema requirements.
 */
export const formatCampaignDataForSubmit = (formData) => {
    // Create a deep copy so we don't mutate the original form state
    const payload = JSON.parse(JSON.stringify(formData));
    const type = payload.scheduleType;

    if (payload.content && payload.content._id) {
        payload.contentId = payload.content._id;

        // Clean up: prevent sending the preview URL and extra data to MongoDB
        delete payload.content;
    }

    // Clean up scheduling object based on the selected scheduleType
    // We only keep and format the relevant array to avoid saving unused data
    const formattedScheduling = {
        startEnd: [],
        recurring: [],
        recurringWithDate: []
    };

    if (type === "startEnd" && payload.scheduling?.startEnd) {
        formattedScheduling.startEnd = payload.scheduling.startEnd.map(slot => ({
            // Convert "YYYY-MM-DDThh:mm" to a standard Date object
            startTime: new Date(slot.startTime),
            endTime: new Date(slot.endTime)
        }));
    }

    if (type === "recurring" && payload.scheduling?.recurring) {
        formattedScheduling.recurring = payload.scheduling.recurring.map(slot => ({
            days: applyDoWDefaults(slot.days || {}),
            // Convert "HH:mm" string to Number (minutes)
            startTime: timeStringToMinutes(slot.startTime),
            endTime: timeStringToMinutes(slot.endTime)
        }));
    }

    if (type === "recurringWithDate" && payload.scheduling?.recurringWithDate) {
        formattedScheduling.recurringWithDate = payload.scheduling.recurringWithDate.map(slot => ({
            days: applyDoWDefaults(slot.days || {}),
            // Convert "YYYY-MM-DD" string to Number (Unix Timestamp in milliseconds)
            startDate: new Date(slot.startDate).getTime(),
            endDate: new Date(slot.endDate).getTime(),
            // Convert "HH:mm" string to Number (minutes)
            startTime: timeStringToMinutes(slot.startTime),
            endTime: timeStringToMinutes(slot.endTime)
        }));
    }

    payload.scheduling = formattedScheduling;

    return payload;
};

/**
 * Converts total minutes since midnight back to "HH:mm".
 * e.g., 870 -> "14:30"
 */
const minutesToTimeString = (totalMinutes) => {
    if (typeof totalMinutes !== 'number') return "00:00";
    const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
    const minutes = (totalMinutes % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

/**
 * Converts a Unix timestamp (Number) or Date string to "YYYY-MM-DD" for type="date"
 */
const timestampToDateString = (timestamp) => {
    if (!timestamp) return "";
    const d = new Date(timestamp);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Converts a Date string to "YYYY-MM-DDTHH:mm" for type="datetime-local"
 */
const dateToDatetimeLocalString = (dateInput) => {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formats the raw Mongoose document so react-hook-form inputs can read it.
 */
export const formatCampaignDataForForm = (apiData) => {
    // Deep clone to avoid mutating the original fetched data
    const data = JSON.parse(JSON.stringify(apiData));

    if (!data.scheduling) return data;

    const type = data.scheduleType;

    if (type === "startEnd" && data.scheduling.startEnd) {
        data.scheduling.startEnd = data.scheduling.startEnd.map(slot => ({
            ...slot,
            startTime: dateToDatetimeLocalString(slot.startTime),
            endTime: dateToDatetimeLocalString(slot.endTime)
        }));
    }

    if (type === "recurring" && data.scheduling.recurring) {
        data.scheduling.recurring = data.scheduling.recurring.map(slot => ({
            ...slot,
            startTime: minutesToTimeString(slot.startTime),
            endTime: minutesToTimeString(slot.endTime)
        }));
    }

    if (type === "recurringWithDate" && data.scheduling.recurringWithDate) {
        data.scheduling.recurringWithDate = data.scheduling.recurringWithDate.map(slot => ({
            ...slot,
            startDate: timestampToDateString(slot.startDate),
            endDate: timestampToDateString(slot.endDate),
            startTime: minutesToTimeString(slot.startTime),
            endTime: minutesToTimeString(slot.endTime)
        }));
    }

    return data;
};